// src/modules/Rules/adapters/ConditionApiAdapter.ts
import { HttpClient } from '@adapters/HttpClient';
import { CountriesApiAdapter } from './CountriesApiAdapter';
import { PacksApiAdapter } from './PacksApiAdapter';
import { PaymentMethodsApiAdapter } from './PaymentMethodsApiAdapter';
import { ProductsApiAdapter } from './ProductsApiAdapter';
import { ShopApiAdapter } from './ShopApiAdapter';

export interface Option {
  id: string;
  name: string;
}

export interface Operator {
  id: string;
  name: string;
}

export interface ValueResult {
  valueType: 'select' | 'text' | 'numeric';
  options: Option[];
}

export class ConditionApiAdapter {
  private productsAdapter: ProductsApiAdapter;
  private packsAdapter: PacksApiAdapter;
  private paymentMethodsAdapter: PaymentMethodsApiAdapter;
  private shopAdapter: ShopApiAdapter;
  private countriesAdapter: CountriesApiAdapter;

  constructor(private httpClient: HttpClient) {
    this.productsAdapter = new ProductsApiAdapter(httpClient);
    this.packsAdapter = new PacksApiAdapter(httpClient);
    this.paymentMethodsAdapter = new PaymentMethodsApiAdapter(httpClient);
    this.shopAdapter = new ShopApiAdapter(httpClient);
    this.countriesAdapter = new CountriesApiAdapter(httpClient);
  }

  async getConditionOptions(): Promise<Option[]> {
    return [
      { id: 'payment_method_id', name: 'Método de pago' },
      { id: 'product_id', name: 'Producto' },
      { id: 'shipping_country_id', name: 'País de envío' },
      { id: 'order_amount', name: 'Valor total del pedido' },
      { id: 'pack_id', name: 'Pack' },
      { id: 'lines_quantity', name: 'Cantidad de artículos' },
      { id: 'shop_id', name: 'La tienda' },
      { id: 'shipping_zip_code', name: 'Código postal de destino' },
      { id: 'shipment_method', name: 'Método de envío' },
      { id: 'has_coverage', name: 'Tiene cobertura' },
    ];
  }

  async getConditionOperators(field: string): Promise<Operator[]> {
    const map: Record<string, string[]> = {
      payment_method_id: ['=', '!='],
      product_id: ['=', '!='],
      pack_id: ['=', '!='],
      shop_id: ['=', '!='],
      shipping_country_id: ['=', '!='],
      order_amount: ['=', '!=', '>', '<'],
      lines_quantity: ['=', '!=', '>', '<'],
      shipping_zip_code: ['=', '!=', 'starts_with', 'ends_with', 'contains'],
      shipment_method: ['=', '!=', 'starts_with', 'ends_with', 'contains'],
      has_coverage: ['='],
    };

    const operatorLabels: Record<string, string> = {
      '=': 'Es',
      '!=': 'No es',
      '>': 'Mayor a',
      '<': 'Menor a',
      '>=': 'Mayor o igual a',
      '<=': 'Menor o igual a',
      starts_with: 'Comienza con',
      ends_with: 'Termina con',
      contains: 'Contiene',
      between: 'Entre',
    };

    return (map[field] || []).map((op) => ({
      id: op,
      name: operatorLabels[op] || op,
    }));
  }

  async getConditionValues(field: string): Promise<ValueResult> {
    switch (field) {
      case 'product_id':
        return {
          valueType: 'select',
          options: await this.productsAdapter.getAll(),
        };
      case 'pack_id':
        return {
          valueType: 'select',
          options: await this.packsAdapter.getAll(),
        };
      case 'payment_method_id':
        return {
          valueType: 'select',
          options: await this.paymentMethodsAdapter.getAll(),
        };
      case 'shop_id':
        return {
          valueType: 'select',
          options: await this.shopAdapter.getAll(),
        };
      case 'shipping_country_id':
        return {
          valueType: 'select',
          options: await this.countriesAdapter.getAll(),
        };
      case 'has_coverage':
        // has_coverage requiere selección en dos pasos: primero courier, luego service
        // No devolvemos opciones aquí, se manejará en el componente
        return {
          valueType: 'select',
          options: [],
        };
      case 'order_amount':
        return { valueType: 'numeric', options: [] };
      case 'shipping_zip_code':
      case 'shipment_method':
        return { valueType: 'text', options: [] };
      default:
        return { valueType: 'text', options: [] };
    }
  }
}
